/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class MarqueeTicker extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".marquee-ticker__track");
    if (!this.track) return;

    if (this.hasAttribute("pause-on-hover")) {
      this.addEventListener("mouseenter", this.pause.bind(this));
      this.addEventListener("mouseleave", this.resume.bind(this));
    }

    if (this.hasAttribute("reveal-on-scroll")) {
      this._setupRevealOnScroll();
    } else {
      this._startAnimation();
    }
  }

  _setupRevealOnScroll() {
    if (!window.IntersectionObserver) {
      this._startAnimation();
      return;
    }
    this.track.style.animationPlayState = "paused";
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._startAnimation();
          } else {
            this.track.style.animationPlayState = "paused";
          }
        });
      },
      { rootMargin: "50px 0px", threshold: 0 },
    );
    observer.observe(this);
    this._observer = observer;
  }

  _startAnimation() {
    if (this.track) {
      this.track.style.animationPlayState = "running";
    }
  }

  pause() {
    if (this.track) {
      this.track.style.animationPlayState = "paused";
    }
  }

  resume() {
    if (this.track) {
      this.track.style.animationPlayState = "running";
    }
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }
}
customElements.define("marquee-ticker", MarqueeTicker);

class InfoAccordion extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector(".icon-text");
    this.content = this.querySelector(".info-accordion__content");
    this.toggleButton.addEventListener("click", this.toggle.bind(this));
  }

  toggle() {
    this.isExpanded = !this.isExpanded;
    this.toggleButton.setAttribute("aria-expanded", this.isExpanded);
    this.content.setAttribute("aria-expanded", this.isExpanded);
  }
}

customElements.define("info-accordion", InfoAccordion);

class ProductUpsells extends HTMLElement {
  connectedCallback() {
    this.swiperConfig = JSON.parse(this.getAttribute("data-swiper-config"));
    this.fetchProducts();
  }

  get productId() {
    return this.getAttribute("data-product-id");
  }

  fetchProducts() {
    const url =
      window.Shopify.routes.root +
      `recommendations/products?product_id=${this.productId}&limit=5&section_id=product-upsells&intent=related`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        html
          .querySelector(".swiper")
          .setAttribute(
            "data-swiper-config",
            JSON.stringify(this.swiperConfig),
          );
        this.querySelector(".product-upsells__container").innerHTML =
          html.querySelector(".shopify-section").innerHTML;
      });
  }
}

customElements.define("product-upsells", ProductUpsells);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.swiperConfig = JSON.parse(this.getAttribute("data-swiper-config"));
  }

  connectedCallback() {
    this.swiper = new Swiper(this, this.swiperConfig);
  }
}

customElements.define("slider-component", SliderComponent);

class AtcForm extends HTMLFormElement {
  connectedCallback() {
    this.submitButton = this.querySelector("button[type='submit']");
    this.addEventListener("submit", this.submit.bind(this));
  }

  submit(event) {
    event.preventDefault();
    this.submitButton.setAttribute("disabled", "disabled");
    this.submitButton.setAttribute("aria-busy", "true");
    const formData = new FormData(this);
    const url = window.Shopify.routes.root + `cart/add.js`;
    fetch(url, {
      method: "POST",
      body: formData,
    })
      .then((response) => response.json())
      .then(() => {
        this.submitButton.removeAttribute("disabled");
        this.submitButton.removeAttribute("aria-busy");

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:refresh", {
            bubbles: true,
          }),
        );

        document.getElementById("mini-cart").open = true;
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

customElements.define("atc-form", AtcForm, { extends: "form" });

class ProductConnector extends HTMLElement {
  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute("data-section-id");
  }

  get optionName() {
    return this.getAttribute("data-option");
  }

  connectedCallback() {
    this.selectors = this.querySelectorAll("input");

    // Single-variant products (colour lives on a sibling product via
    // product_family) never render the variant picker, so this script is
    // absent. Fall back to null instead of throwing - rerenderProduct already
    // optional-chains variantJson, and the listeners below must still attach.
    const variantScript = document.querySelector("script[data-variant]");
    this.variantJson = null;

    if (variantScript) {
      try {
        this.variantJson = JSON.parse(variantScript.innerHTML);
      } catch (error) {
        this.variantJson = null;
      }
    }

    this.selectors.forEach((selector) => {
      selector.addEventListener("change", this.rerenderProduct.bind(this));
    });
  }

  findVariantByOptions(product, selectedOptions) {
    console.log(product, selectedOptions);
    if (!product?.variants?.length) return undefined;
    const skipOption = this.optionName;
    
    return product.variants.find((v) => {
      return [1, 2, 3].every((i) => {
        const key = `option${i}`;
        if (key === skipOption) return true;
        const selected = selectedOptions[key];
        const variantVal = v[key];
        return selected == null
          ? variantVal == null || variantVal === ""
          : variantVal === selected;
      });
    });
  }

  rerenderProduct(event) {
    const productHandle = event.target.value;
    const section = this.closest(".shopify-section");

    const selectedOptions = {
      option1: this.variantJson?.option1 ?? null,
      option2: this.variantJson?.option2 ?? null,
      option3: this.variantJson?.option3 ?? null,
    };

    console.log(selectedOptions);

    const productJsonUrl = `${window.Shopify.routes.root}products/${productHandle}.js`;

    fetch(productJsonUrl)
      .then((response) => response.json())
      .then((newProduct) => {
        const matchingVariant = this.findVariantByOptions(
          newProduct,
          selectedOptions,
        );
        const sectionUrl = `${window.Shopify.routes.root}products/${productHandle}?section_id=${this.sectionId}${matchingVariant?.id ? `&variant=${matchingVariant.id}` : ""}`;

        return fetch(sectionUrl)
          .then((res) => res.text())
          .then((html) => ({ html, variantId: matchingVariant?.id }));
      })
      .then(({ html, variantId }) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        const newSection = tempDiv.querySelector(".shopify-section");
        if (newSection) section.innerHTML = newSection.innerHTML;

        const newUrl = new URL(
          `${window.Shopify.routes.root}products/${productHandle}`,
          window.location.origin,
        );
        if (variantId) newUrl.searchParams.set("variant", String(variantId));
        window.history.replaceState({}, "", newUrl.toString());
      });
  }
}

customElements.define("product-connector", ProductConnector);

/**
 * The product gallery renders a desktop and a mobile variant side by side and
 * hides one with CSS. `native-video` autoplays from its constructor regardless
 * of visibility, so without this both copies of the video decode at once.
 * Keeps only the visible gallery's video playing.
 */
class GalleryVideoSync extends HTMLElement {
  connectedCallback() {
    this.sync = this.sync.bind(this);

    // Siblings may still be parsing when this fires, so defer the first pass.
    this.frame = requestAnimationFrame(this.sync);
    window.addEventListener("resize", this.sync);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    window.removeEventListener("resize", this.sync);
  }

  sync() {
    const scope = this.closest(".shopify-section") || document;

    scope.querySelectorAll(".product-gallery native-video[autoplay]").forEach((video) => {
      // offsetParent is null once the element or an ancestor is display:none.
      if (video.offsetParent === null) {
        this.pauseWhenPlaying(video);
      } else {
        video.play();
      }
    });
  }

  /**
   * native-video autoplays from its constructor, so a pause() issued while that
   * play() promise is still pending rejects with an AbortError. Wait for
   * playback to actually start before pausing.
   */
  pauseWhenPlaying(video) {
    const inner = video.querySelector("video");

    if (!inner || inner.paused) {
      return;
    }

    if (inner.readyState >= 3) {
      inner.pause();
      return;
    }

    inner.addEventListener(
      "playing",
      () => {
        if (video.offsetParent === null) {
          inner.pause();
        }
      },
      { once: true },
    );
  }
}

customElements.define("gallery-video-sync", GalleryVideoSync);

class ComparisonSliderComponent extends HTMLElement {
  constructor() {
    super();
    this._boundSync = this.sync.bind(this);
  }

  connectedCallback() {
    this.mediaWrapper = this.querySelector("[data-comparison-media-wrapper]");
    this.slider = this.querySelector(".cs-slider");
    if (!this.mediaWrapper || !this.slider) return;

    this.sync();
    this.slider.addEventListener("input", this._boundSync);
    this.slider.addEventListener("pointerdown", this._boundSync);
  }

  disconnectedCallback() {
    if (this.slider) {
      this.slider.removeEventListener("input", this._boundSync);
      this.slider.removeEventListener("pointerdown", this._boundSync);
    }
  }

  sync() {
    if (!this.mediaWrapper || !this.slider) return;
    const min = Number(this.slider.min);
    const max = Number(this.slider.max);
    const val = (Number(this.slider.value) - min) / (max - min);
    const compareValue = Math.round(val * 100);
    this.mediaWrapper.style.setProperty("--compare", String(compareValue));
  }
}

if (!customElements.get("comparison-slider-component")) {
  customElements.define(
    "comparison-slider-component",
    ComparisonSliderComponent,
  );
}

class CountdownTimer extends HTMLElement {
  constructor() {
    super();
    this.countdownDate = Number(this.dataset.date) * 1000;
    this.daysElement = this.querySelector("#CountdownDays");
    this.hoursElement = this.querySelector("#CountdownHours");
    this.minutesElement = this.querySelector("#CountdownMinutes");
    this.secondsElement = this.querySelector("#CountdownSeconds");
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.countdownInterval = setInterval(() => {
      this.updateCountdown();
    }, 1000);
  }

  updateCountdown() {
    const now = new Date().getTime();
    const timeDifference = this.countdownDate - now;

    if (timeDifference < 0) {
      clearInterval(this.countdownInterval);
      this.daysElement.innerText = "00";
      this.hoursElement.innerText = "00";
      this.minutesElement.innerText = "00";
      this.secondsElement.innerText = "00";
      return;
    }

    const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor(
      (timeDifference % (1000 * 60 * 60)) / (1000 * 60)
    );
    const seconds = Math.floor((timeDifference % (1000 * 60)) / 1000);

    this.daysElement.innerText = this.formatTime(days);
    this.hoursElement.innerText = this.formatTime(hours);
    this.minutesElement.innerText = this.formatTime(minutes);
    this.secondsElement.innerText = this.formatTime(seconds);
  }

  formatTime(timeUnit) {
    return timeUnit < 10 ? "0" + timeUnit : timeUnit;
  }
}

customElements.define("countdown-timer", CountdownTimer);

class SliderElement extends HTMLElement {
  constructor() {
    super();
    this.sliderConfig = this.dataset.sliderConfig
      ? JSON.parse(this.dataset.sliderConfig)
      : null;
  }

  connectedCallback() {
    if (!this.sliderConfig) return;

    new Swiper(this, this.sliderConfig);

    if (this.dataset.mainSlider) {
      const mainEl = document.querySelector(this.dataset.mainSlider);
      mainEl.swiper.thumbs.swiper = this.swiper;
      mainEl.swiper.thumbs.init();
      mainEl.swiper.thumbs.update(true);
    }
  }

  disconnectedCallback() {
    if (!this.sliderConfig) return;

    this.swiper.destroy(true, true);
  }
}

customElements.define("slider-element", SliderElement);

class SyncCheckbox extends HTMLElement {
  connectedCallback() {
    this._checkbox = this.querySelector('input[type="checkbox"]');
    if (!this._checkbox) return;

    this._onChange = () => {
      const group = this.dataset.group;
      document.querySelectorAll(`sync-checkbox[data-group="${group}"]`).forEach(el => {
        const checkbox = el.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = this._checkbox.checked;
      });
    };

    this._checkbox.addEventListener('change', this._onChange);
  }

  disconnectedCallback() {
    if (this._checkbox) {
      this._checkbox.removeEventListener('change', this._onChange);
    }
  }
}

customElements.define('sync-checkbox', SyncCheckbox);

/* ───── Story Menu (Instagram-style stories) ───── */

class StoryMenu extends HTMLElement {
  static DURATION = 5000;

  connectedCallback() {
    this.viewer = this.querySelector('.story-viewer');
    if (!this.viewer) return;

    this.container = this.viewer.querySelector('.story-viewer__container');
    this.slides = [...this.viewer.querySelectorAll('.story-viewer__slide')];
    this.bars = [...this.viewer.querySelectorAll('.story-viewer__progress-bar')];
    this.items = [...this.querySelectorAll('.story-menu__item')];
    this.activeIndex = 0;
    this._timer = null;
    this._paused = false;

    this.items.forEach((btn) => {
      btn.addEventListener('click', () => this.open(parseInt(btn.dataset.storyIndex, 10)));
    });

    this.viewer.querySelector('[data-action="close-story"]')
      .addEventListener('click', () => this.close());
    this.viewer.querySelector('.story-viewer__overlay')
      .addEventListener('click', () => this.close());

    this.viewer.querySelectorAll('.story-viewer__slide-content').forEach((el) => {
      el.addEventListener('touchstart', () => this._pause(), { passive: true });
      el.addEventListener('touchend', () => this._resume(), { passive: true });
      el.addEventListener('touchcancel', () => this._resume(), { passive: true });
      el.addEventListener('scroll', () => {
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
        el.classList.toggle('is-scrolled-end', atBottom);
      }, { passive: true });
    });

    this._onKey = (e) => {
      if (this.viewer.getAttribute('aria-hidden') !== 'false') return;
      if (e.key === 'ArrowLeft') this.prev();
      else if (e.key === 'ArrowRight') this.next();
      else if (e.key === 'Escape') this.close();
    };
    document.addEventListener('keydown', this._onKey);

    this._initSwipe();
  }

  disconnectedCallback() {
    this._stopTimer();
    if (this._onKey) document.removeEventListener('keydown', this._onKey);
  }

  open(index) {
    this.activeIndex = index;
    this.viewer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('scroll-lock');
    this._goTo(index);
  }

  close() {
    this._stopTimer();
    this.viewer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('scroll-lock');
  }

  prev() {
    if (this.activeIndex > 0) {
      this._goTo(this.activeIndex - 1);
    }
  }

  next() {
    if (this.activeIndex < this.slides.length - 1) {
      this._goTo(this.activeIndex + 1);
    } else {
      this.close();
    }
  }

  _goTo(index) {
    this._stopTimer();
    this._paused = false;
    this.activeIndex = index;

    this.slides.forEach((slide, i) => {
      slide.toggleAttribute('data-active', i === index);
    });

    this.bars.forEach((bar, i) => {
      const fill = bar.querySelector('.story-viewer__progress-fill');
      fill.style.transition = 'none';
      fill.style.width = '';
      bar.removeAttribute('data-state');
      if (i < index) bar.setAttribute('data-state', 'done');
    });

    requestAnimationFrame(() => {
      const fill = this.bars[index]?.querySelector('.story-viewer__progress-fill');
      if (fill) fill.style.transition = '';
      this.bars[index]?.setAttribute('data-state', 'active');
    });

    this._startTimer();
  }

  _startTimer() {
    this._stopTimer();
    this._timer = setTimeout(() => this.next(), StoryMenu.DURATION);
  }

  _stopTimer() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  _pause() {
    if (this._paused) return;
    this._paused = true;
    this._stopTimer();
    const bar = this.bars[this.activeIndex];
    if (bar) {
      const fill = bar.querySelector('.story-viewer__progress-fill');
      const w = fill.getBoundingClientRect().width;
      const parentW = bar.getBoundingClientRect().width;
      this._pausedPercent = parentW > 0 ? (w / parentW) * 100 : 0;
      fill.style.transition = 'none';
      fill.style.width = `${this._pausedPercent}%`;
    }
  }

  _resume() {
    if (!this._paused) return;
    this._paused = false;
    const bar = this.bars[this.activeIndex];
    if (bar) {
      const fill = bar.querySelector('.story-viewer__progress-fill');
      const remaining = 1 - (this._pausedPercent / 100);
      const remainingMs = remaining * StoryMenu.DURATION;
      fill.style.transition = `width ${remainingMs}ms linear`;
      fill.style.width = '100%';
      this._timer = setTimeout(() => this.next(), remainingMs);
    }
  }

  _addTapHold(el, tapFn) {
    let holdTimer = null;
    let held = false;
    let active = false;

    const onDown = (e) => {
      e.preventDefault();
      held = false;
      active = true;
      holdTimer = setTimeout(() => {
        held = true;
        this._pause();
      }, 200);
    };

    const onUp = () => {
      if (!active) return;
      active = false;
      clearTimeout(holdTimer);
      if (held) {
        this._resume();
      } else {
        tapFn();
      }
      held = false;
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('pointerleave', onUp);
  }

  _initSwipe() {
    const overlay = this.viewer.querySelector('.story-viewer__overlay');
    const imageContainers = this.viewer.querySelectorAll('.story-viewer__slide-image-container');
    let startX = 0;
    let startY = 0;
    let lastY = 0;
    let tracking = false;
    let direction = null;
    let dragOffset = 0;
    let holdTimer = null;
    let held = false;

    const onStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      lastY = startY;
      tracking = true;
      direction = null;
      dragOffset = 0;
      held = false;

      this.container.style.transition = 'none';
      this.container.style.willChange = 'transform';
      overlay.style.transition = 'none';
      overlay.style.willChange = 'opacity';

      holdTimer = setTimeout(() => {
        held = true;
        this._pause();
      }, 200);
    };

    const onMove = (e) => {
      if (!tracking) return;
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const dx = x - startX;
      const dy = y - startY;

      if (!direction && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        direction = Math.abs(dy) > Math.abs(dx) ? 'vertical' : 'horizontal';
        clearTimeout(holdTimer);
        if (!held) this._pause();
      }

      if (direction === 'vertical') {
        dragOffset = Math.max(0, dy);
        if (dragOffset > 0) {
          e.preventDefault();
          this.container.style.transform = `translateY(${dragOffset}px)`;
          overlay.style.opacity = Math.max(0, 1 - dragOffset / 350);
        }
      }

      lastY = y;
    };

    const onEnd = (e) => {
      if (!tracking) return;
      tracking = false;
      clearTimeout(holdTimer);

      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const dx = endX - startX;
      const dy = endY - startY;
      const velocity = endY - lastY;
      const isTap = !direction && Math.abs(dx) < 10 && Math.abs(dy) < 10;

      this.container.style.willChange = '';
      overlay.style.willChange = '';
      this.container.style.transition = 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)';
      overlay.style.transition = 'opacity 0.3s ease';

      if (direction === 'vertical' && (dragOffset > 100 || velocity > 8)) {
        this.container.style.transform = 'translateY(100%)';
        overlay.style.opacity = '0';
        setTimeout(() => {
          this.viewer.style.transition = 'none';
          this.close();
          this.container.style.cssText = '';
          overlay.style.cssText = '';
          requestAnimationFrame(() => {
            this.viewer.style.transition = '';
          });
        }, 300);
        return;
      }

      this.container.style.transform = '';
      overlay.style.opacity = '';
      setTimeout(() => {
        this.container.style.cssText = '';
        overlay.style.cssText = '';
      }, 300);

      if (held) {
        this._resume();
        return;
      }

      if (direction === 'horizontal' && Math.abs(dx) > 50) {
        if (dx < 0) this.next();
        else this.prev();
      } else if (isTap) {
        const target = e.target.closest('.story-viewer__slide-image-container');
        if (target) {
          const rect = target.getBoundingClientRect();
          const tapX = startX - rect.left;
          if (tapX < rect.width * 0.35) this.prev();
          else this.next();
        }
      }

      this._resume();
    };

    imageContainers.forEach((el) => {
      el.addEventListener('touchstart', onStart, { passive: true });
      el.addEventListener('touchmove', onMove, { passive: false });
      el.addEventListener('touchend', onEnd, { passive: true });
      el.addEventListener('touchcancel', onEnd, { passive: true });
    });
  }
}

customElements.define('story-menu', StoryMenu);

class ShowMore extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('.product-gallery');
    this.button = this.querySelector('button');
    this.button.addEventListener('click', this.reveal.bind(this));
  }

  reveal() {
    if (!this.gallery) return;

    this.gallery.querySelectorAll('.product-gallery__item.hidden').forEach(item => {
      item.classList.remove('hidden');
    });

    this.classList.add('hidden');
  }
}

customElements.define('show-more', ShowMore);

class AnnouncementCountdown extends HTMLElement {
  connectedCallback() {
    this._unitEls = this.querySelectorAll('.announcement-bar__countdown-unit');
    this.tick();
    this._interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  getBerlinOffsetMinutes() {
    const now = new Date();
    const utcDate = new Date(now.toLocaleString('en-US', { timeZone: 'UTC' }));
    const berlinDate = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
    return Math.round((berlinDate - utcDate) / 60000);
  }

  computeTarget() {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const berlinDateStr = fmt.format(new Date());
    const offsetMins = this.getBerlinOffsetMinutes();
    const offsetSign = offsetMins >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMins);
    const offsetHH = String(Math.floor(absOffset / 60)).padStart(2, '0');
    const offsetMM = String(absOffset % 60).padStart(2, '0');
    return new Date(`${berlinDateStr}T22:15:00${offsetSign}${offsetHH}:${offsetMM}`);
  }

  pad(n) {
    return String(n).padStart(2, '0');
  }

  tick() {
    const now = new Date();
    const target = this.computeTarget();
    const inlineSpan = this.closest('.announcement-bar__countdown-inline');
    const diff = target - now;

    if (diff <= 0) {
      if (inlineSpan) inlineSpan.style.display = 'none';
      return;
    }

    if (inlineSpan) inlineSpan.style.display = 'inline';

    const totalSeconds = Math.floor(diff / 1000);
    const hh = Math.floor(totalSeconds / 3600);
    const mm = Math.floor((totalSeconds % 3600) / 60);
    const ss = totalSeconds % 60;

    if (this._unitEls && this._unitEls.length === 3) {
      this._unitEls[0].textContent = this.pad(hh);
      this._unitEls[1].textContent = this.pad(mm);
      this._unitEls[2].textContent = this.pad(ss);
    }
  }
}

customElements.define('announcement-countdown', AnnouncementCountdown);
/**
 * Keeps the price on the add-to-cart button equal to what the customer will
 * actually be charged.
 *
 * The label was written to always show the unit price, which was accurate
 * while the quantity was fixed at one. With a stepper on the page a static
 * price reads as the total and understates the real charge, so it is
 * multiplied here and kept in step with variant changes.
 */
class AddToCartTotal extends HTMLElement {
  connectedCallback() {
    this.update = this.update.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);

    this.root.addEventListener("input", this.update);
    this.root.addEventListener("change", this.update);
    document.addEventListener("variant:changed", this.onVariantChange);

    // Children may still be parsing when this fires.
    this.frame = requestAnimationFrame(this.update);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.root.removeEventListener("input", this.update);
    this.root.removeEventListener("change", this.update);
    document.removeEventListener("variant:changed", this.onVariantChange);
  }

  get root() {
    // Rendered as an empty marker inside the form so it can reach both the
    // stepper and the button without wrapping either of them.
    return this.closest(".product-form") || document;
  }

  get priceElement() {
    return this.root.querySelector(".add-to-cart__price[data-unit-price]");
  }

  onVariantChange(event) {
    const price = event.detail?.variant?.price;
    const el = this.priceElement;

    if (el && typeof price === "number") {
      el.dataset.unitPrice = String(price);
    }

    this.update();
  }

  update() {
    const el = this.priceElement;
    if (!el) {
      return;
    }

    const unit = Number(el.dataset.unitPrice);
    if (!Number.isFinite(unit)) {
      return;
    }

    const input = this.root.querySelector('[data-block-type="quantity-selector"] input[type="number"]');
    const quantity = Math.max(1, parseInt(input?.value, 10) || 1);

    el.textContent = this.money(unit * quantity);
  }

  /**
   * theme.js keeps its formatMoney private, so the shop's format string is
   * applied here rather than reaching for a helper that is not exposed.
   */
  money(cents) {
    const format = window.themeVariables?.settings?.moneyFormat || "€{{amount_with_comma_separator}}";
    const useComma = format.includes("comma_separator");
    const thousands = useComma ? "." : ",";
    const decimal = useComma ? "," : ".";
    const noDecimals = format.includes("no_decimals");

    const amount = (cents / 100).toFixed(noDecimals ? 0 : 2);
    const [whole, fraction] = amount.split(".");
    const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

    return format.replace(
      /\{\{\s*\w+\s*\}\}/,
      fraction ? `${grouped}${decimal}${fraction}` : grouped,
    );
  }
}

customElements.define("add-to-cart-total", AddToCartTotal);

/**
 * Collapses the product description to a couple of lines with a toggle.
 *
 * The advantages are carried by the bullets underneath, so the prose above
 * them only needs to set the scene. The button is hidden unless the text is
 * actually taller than the clamp, so short descriptions get no toggle at all.
 */
class ReadMore extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector("[data-pdp-description-text]");
    this.button = this.querySelector("[data-pdp-description-toggle]");
    this.label = this.querySelector("[data-pdp-description-label]");
    if (!this.text || !this.button) return;

    this.labels = {
      more: this.label?.textContent.trim() || "Mehr Infos",
      less: "Weniger anzeigen",
    };

    this.toggle = this.toggle.bind(this);
    this.measure = this.measure.bind(this);
    this.button.addEventListener("click", this.toggle);
    window.addEventListener("resize", this.measure);

    // Fonts and images can still be settling, which changes the height.
    this.frame = requestAnimationFrame(this.measure);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.frame);
    this.button?.removeEventListener("click", this.toggle);
    window.removeEventListener("resize", this.measure);
  }

  measure() {
    if (this.classList.contains("is-open")) return;
    const overflowing = this.text.scrollHeight - this.text.clientHeight > 2;
    this.button.hidden = !overflowing;
  }

  toggle() {
    const open = this.classList.toggle("is-open");
    this.button.setAttribute("aria-expanded", String(open));
    if (this.label) this.label.textContent = open ? this.labels.less : this.labels.more;
  }
}

customElements.define("read-more", ReadMore);
